import React from 'react'
import { observer } from 'mobx-react'
import cn from 'classnames'

import Mousetrap from 'components/mousetrap/Mousetrap'
import Modal from 'components/modal/Modal'
import { IDropdownItem } from './Dropdown'

import styles from './dropdownMenu.scss'

interface IProps {
  target: React.RefObject<HTMLDivElement>
  items: IDropdownItem[]
  onSelect: (item: IDropdownItem) => void
  onBlur?: () => void
}

interface IState {
  highlightIndex: number
}

class DropdownMenu extends React.PureComponent<IProps, IState> {
  menuRef: React.RefObject<HTMLDivElement> = React.createRef()
  itemRefs: React.RefObject<HTMLDivElement>[] = []

  constructor(props: IProps) {
    super(props)

    this.state = {
      highlightIndex: -1,
    }
    this.itemRefs = props.items.map(() => React.createRef())
  }

  componentDidMount() {
    const targetBounds = this.props.target.current?.getBoundingClientRect()
    if (!targetBounds) {
      return
    }

    this.menuRef.current?.focus()
  }

  handleHighlightNext = () => {
    const { items } = this.props

    this.setState(
      state => {
        if (state.highlightIndex === items.length - 1) {
          return { highlightIndex: 0 }
        }

        return { highlightIndex: state.highlightIndex + 1 }
      },
      () => {
        this.itemRefs[this.state.highlightIndex].current?.scrollIntoView(false)
        this.itemRefs[this.state.highlightIndex].current?.scrollIntoView({
          block: 'nearest',
        }) // This is only supported on Chromium, and is more relevant. On Firefox, this is a no-op so the previous `scrollIntoView` would apply
      }
    )
  }

  handleHighlightPrevious = () => {
    const { items } = this.props

    this.setState(
      state => {
        if (state.highlightIndex === 0 || state.highlightIndex === -1) {
          return { highlightIndex: items.length - 1 }
        }

        return { highlightIndex: state.highlightIndex - 1 }
      },
      () => {
        this.itemRefs[this.state.highlightIndex].current?.scrollIntoView(false)
        this.itemRefs[this.state.highlightIndex].current?.scrollIntoView({
          block: 'nearest',
        }) // This is only supported on Chromium, and is more relevant. On Firefox, this is a no-op so the previous `scrollIntoView` would apply
      }
    )
  }

  handleSelect = () => {
    const { highlightIndex } = this.state
    const { items, onSelect } = this.props

    if (highlightIndex < 0 || highlightIndex >= items.length) {
      return
    }

    onSelect(items[highlightIndex])
  }

  handleCancel = (e: ExtendedKeyboardEvent) => {
    e.preventDefault()
  }

  render() {
    const { highlightIndex } = this.state
    const { target, items, onSelect, onBlur } = this.props

    return (
      <Modal
        className={styles.container}
        maskClassName={styles.mask}
        domElementId="dropdown-root"
        target={target}
        targetOffsetY={0}
        darken={false}
        onClickOutside={onBlur}
      >
        <>
          <div ref={this.menuRef} className={styles.container} tabIndex={1}>
            {items.map((i, idx) => (
              <div
                ref={this.itemRefs[idx]}
                key={i.id}
                data-cy="dropdown-menu__item"
                className={cn(styles.item, {
                  [styles.highlighted]: highlightIndex === idx,
                })}
                onClick={() => onSelect(i)}
              >
                {i.label}
              </div>
            ))}
          </div>

          <Mousetrap
            keys="up"
            target={this.menuRef}
            onMatch={this.handleHighlightPrevious}
          />
          <Mousetrap
            keys="down"
            target={this.menuRef}
            onMatch={this.handleHighlightNext}
          />
          <Mousetrap
            keys="esc"
            target={this.menuRef}
            onMatch={() => onBlur?.()}
          />

          <Mousetrap
            keys="enter"
            target={this.menuRef}
            onMatch={this.handleSelect}
          />
        </>
      </Modal>
    )
  }
}

export default observer(DropdownMenu)
